import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/TranslationContext';
import { API_ENDPOINTS } from '../utils/config';
import '../styles/ApprovedSuppliersModal.css';

interface ApprovedSuppliersModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectSuppliers: (suppliers: string[]) => void;
  selectedSuppliers: string[];
  userCompanyId?: string;  // User'ın company ID'si
}

interface Supplier {
  _id: string;
  name: string;
  emails: string[];
  phone: string;
  website: string;
  region: string;
  related_company_id?: string;
}



const ApprovedSuppliersModal: React.FC<ApprovedSuppliersModalProps> = ({
  isOpen,
  onClose,
  onSelectSuppliers,
  selectedSuppliers,
  userCompanyId
}) => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');

  // Fetch suppliers when modal opens
  useEffect(() => {
    if (isOpen && userCompanyId) {
      fetchApprovedSuppliers(userCompanyId);
    } else {
      setSuppliers([]);
    }
  }, [isOpen, userCompanyId]);



  const fetchApprovedSuppliers = async (companyId: string) => {
    try {
      setIsLoading(true);
      console.log('🔍 Fetching approved suppliers for company:', companyId);
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/suppliers/approved`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('🔍 Approved suppliers response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('🔍 Approved suppliers data:', data);
        setSuppliers(data.suppliers || []);
      } else {
        const errorText = await response.text();
        console.error('🔍 Approved suppliers fetch failed:', response.status, errorText);
        setError('Failed to fetch approved suppliers');
      }
    } catch (err) {
      setError('Error fetching approved suppliers');
      console.error('Error fetching approved suppliers:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSupplierToggle = (supplierName: string) => {
    const isSelected = selectedSuppliers.includes(supplierName);
    if (isSelected) {
      onSelectSuppliers(selectedSuppliers.filter(name => name !== supplierName));
    } else {
      onSelectSuppliers([...selectedSuppliers, supplierName]);
    }
  };

  const handleSelectAll = () => {
    const allSupplierNames = suppliers.map(s => s.name);
    onSelectSuppliers(allSupplierNames);
  };

  const handleClearAll = () => {
    onSelectSuppliers([]);
  };

  const handleConfirm = () => {
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="approved-suppliers-modal">
        <div className="modal-header">
          <h2>{t('common.approved_suppliers')} ({suppliers.length})</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>


                  <div className="approved-suppliers-content">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}



          {/* Quick Actions */}
          {suppliers.length > 0 && (
            <div className="quick-actions">
              <button
                type="button"
                className="quick-action-btn select-all"
                onClick={handleSelectAll}
              >
                {t('common.select_all')}
              </button>
              <button
                type="button"
                className="quick-action-btn clear-all"
                onClick={handleClearAll}
              >
                {t('common.clear_all')}
              </button>
            </div>
          )}

          {/* Suppliers Table */}
          {isLoading ? (
            <div className="loading-message">
              {t('common.loading')}...
            </div>
          ) : suppliers.length > 0 ? (
            <div className="suppliers-table-container">
              <table className="suppliers-table">
                <thead>
                  <tr>
                    <th style={{ width: '50px' }}>
                      <input
                        type="checkbox"
                        className="supplier-checkbox"
                        checked={selectedSuppliers.length === suppliers.length && suppliers.length > 0}
                        onChange={selectedSuppliers.length === suppliers.length ? handleClearAll : handleSelectAll}
                      />
                    </th>
                    <th>{t('common.supplier_name')}</th>
                    <th>{t('common.supplier_email')}</th>
                    <th>{t('common.supplier_phone')}</th>
                    <th>{t('common.supplier_region')}</th>
                  </tr>
                </thead>
                <tbody>
                  {suppliers.map(supplier => (
                    <tr key={supplier._id}>
                      <td>
                        <input
                          type="checkbox"
                          className="supplier-checkbox"
                          checked={selectedSuppliers.includes(supplier.name)}
                          onChange={() => handleSupplierToggle(supplier.name)}
                        />
                      </td>
                      <td className="supplier-name">{supplier.name}</td>
                      <td className="supplier-email">
                        {supplier.emails.length > 0 && (
                          <div className="email-list">
                            {supplier.emails.map((email, emailIndex) => (
                              <div key={emailIndex} className="email-item">
                                {email}
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="supplier-phone">{supplier.phone || '-'}</td>
                      <td className="supplier-region">{supplier.region || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-suppliers-message">
              {t('common.no_approved_suppliers_found')}
            </div>
          )}

          {/* Selected Count */}
          {selectedSuppliers.length > 0 && (
            <div className="selected-count">
              {t('common.selected_suppliers')}: {selectedSuppliers.length}
            </div>
          )}
        </div>

        <div className="modal-actions">
          <button
            type="button"
            className="cancel-button"
            onClick={onClose}
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="confirm-button"
            onClick={handleConfirm}
          >
            {t('common.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ApprovedSuppliersModal;
